import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, ScanFace } from "lucide-react";
import { carregarModelos, extrairDescritor, FalhaExtracao, type ResultadoExtracao } from "./faceEmbedding";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  titulo: string;
  descricao?: string;
  /** Rótulo do botão de captura. */
  rotuloCaptura?: string;
  /** Chamado com o embedding extraído. Deve lançar em caso de erro para exibir a mensagem. */
  onCapturar: (r: ResultadoExtracao) => Promise<void>;
}

type Fase = "iniciando" | "pronto" | "capturando" | "processando" | "erro";

/**
 * Captura facial pela webcam: abre a câmera, carrega o modelo on-device e, ao
 * capturar, extrai o descritor (128-d) e o entrega via onCapturar. A imagem
 * nunca sai do dispositivo — só o vetor. Encerra a câmera ao fechar.
 */
export function CapturaFacial({ open, onOpenChange, titulo, descricao, rotuloCaptura = "Capturar", onCapturar }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [fase, setFase] = useState<Fase>("iniciando");
  const [erro, setErro] = useState<string>("");

  const encerrarCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!open) { encerrarCamera(); return; }
    let cancelado = false;
    setFase("iniciando");
    setErro("");

    (async () => {
      try {
        // Modelo e câmera em paralelo — o que terminar por último libera a captura.
        const streamP = navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 480 }, audio: false });
        await carregarModelos();
        const stream = await streamP;
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => { /* autoplay pode exigir gesto; o vídeo é muted */ });
        }
        setFase("pronto");
      } catch (e: any) {
        if (cancelado) return;
        setFase("erro");
        setErro(
          e?.name === "NotAllowedError" ? "Permissão de câmera negada. Autorize o acesso à câmera."
          : e?.name === "NotFoundError" ? "Nenhuma câmera encontrada neste dispositivo."
          : "Falha ao carregar câmera/modelo. Verifique a conexão (primeiro uso baixa ~7 MB).",
        );
      }
    })();

    return () => { cancelado = true; encerrarCamera(); };
  }, [open, encerrarCamera]);

  const capturar = async () => {
    if (!videoRef.current) return;
    setFase("processando");
    setErro("");
    try {
      const r = await extrairDescritor(videoRef.current);
      await onCapturar(r);
      onOpenChange(false);
    } catch (e: any) {
      setFase("pronto");
      setErro(e instanceof FalhaExtracao ? e.message : (e?.message ?? "Falha ao processar a imagem."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanFace className="h-5 w-5" />{titulo}</DialogTitle>
        </DialogHeader>

        {descricao && <p className="text-sm text-muted-foreground">{descricao}</p>}

        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} muted playsInline className="h-full w-full object-cover [transform:scaleX(-1)]" />
          {(fase === "iniciando" || fase === "processando") && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">{fase === "iniciando" ? "Preparando câmera e modelo…" : "Analisando rosto…"}</span>
            </div>
          )}
          <div className="pointer-events-none absolute inset-8 rounded-[50%] border-2 border-white/40" />
        </div>

        {erro && <p className="text-sm text-destructive">{erro}</p>}
        {fase === "erro" && !erro && <p className="text-sm text-destructive">Câmera indisponível.</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={capturar} disabled={fase !== "pronto"}>
            {fase === "processando" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
            {rotuloCaptura}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
